import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Heart, Plus, Search, Edit, Trash2, Save, Loader2, RefreshCw, Users, Baby, User, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient as api } from '../../lib/api';

const CATEGORIAS = ['Infância', 'Idosos', 'Deficiência', 'Família', 'Saúde Mental', 'Sem-Abrigo', 'Outros'];
const PUBLICO_ALVO = ['Crianças', 'Jovens', 'Adultos', 'Idosos', 'Famílias', 'Todos'];

// Icons for demographic groups
const GRUPO_ICONS = {
  'bebes': Baby,
  'criancas': GraduationCap,
  'jovens': User,
  'adultos': Users,
  'idosos': Heart
};

const GRUPO_COLORS = {
  'bebes': 'bg-pink-100 text-pink-700 border-pink-200',
  'criancas': 'bg-blue-100 text-blue-700 border-blue-200',
  'jovens': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'adultos': 'bg-green-100 text-green-700 border-green-200',
  'idosos': 'bg-purple-100 text-purple-700 border-purple-200'
};

export default function ValenciasPage() {
  const [valencias, setValencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('Todas');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingValencia, setEditingValencia] = useState(null);
  
  // Demographics state
  const [faixasEtarias, setFaixasEtarias] = useState([]);
  const [faixasPorGrupo, setFaixasPorGrupo] = useState({});
  const [loadingFaixas, setLoadingFaixas] = useState(false);
  
  const [formData, setFormData] = useState({
    codigo_crm: '',
    nome: '',
    descricao: '',
    categoria: '',
    publico_alvo: '',
    capacidade_padrao: 50,
    ativo: true,
    faixas_etarias_permitidas: [] // New field for demographic ranges
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadValencias();
    loadFaixasEtarias();
  }, []);
  
  const loadFaixasEtarias = async () => {
    setLoadingFaixas(true);
    try {
      const [faixasRes, gruposRes] = await Promise.all([
        api.get('/demografia/faixas-etarias'),
        api.get('/demografia/faixas-etarias/grupos')
      ]);
      setFaixasEtarias(faixasRes.data || []);
      setFaixasPorGrupo(gruposRes.data || {});
    } catch (error) {
      console.error('Error loading faixas:', error);
    } finally {
      setLoadingFaixas(false);
    }
  };

  const loadValencias = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/valencias`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setValencias(data);
      }
    } catch (error) {
      console.error('Error loading valencias:', error);
      toast.error('Erro ao carregar respostas sociais');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingValencia(null);
    setFormData({
      codigo_crm: '',
      nome: '',
      descricao: '',
      categoria: '',
      publico_alvo: '',
      capacidade_padrao: 50,
      ativo: true,
      faixas_etarias_permitidas: []
    });
    setModalOpen(true);
  };

  const openEdit = (valencia) => {
    setEditingValencia(valencia);
    setFormData({
      codigo_crm: valencia.codigo_crm || '',
      nome: valencia.nome || '',
      descricao: valencia.descricao || '',
      categoria: valencia.categoria || '',
      publico_alvo: valencia.publico_alvo || '',
      capacidade_padrao: valencia.capacidade_padrao || 50,
      ativo: valencia.ativo !== false,
      faixas_etarias_permitidas: valencia.faixas_etarias_permitidas || []
    });
    setModalOpen(true);
  };

  // Toggle faixa etaria selection
  const toggleFaixaEtaria = (faixaCodigo) => {
    setFormData(prev => {
      const current = prev.faixas_etarias_permitidas || [];
      if (current.includes(faixaCodigo)) {
        return { ...prev, faixas_etarias_permitidas: current.filter(f => f !== faixaCodigo) };
      } else {
        return { ...prev, faixas_etarias_permitidas: [...current, faixaCodigo] };
      }
    });
  };
  
  // Select all faixas in a grupo
  const toggleGrupo = (grupo, faixasDoGrupo) => {
    const codigos = faixasDoGrupo.map(f => f.codigo);
    const current = formData.faixas_etarias_permitidas || [];
    const allSelected = codigos.every(c => current.includes(c));
    
    if (allSelected) {
      // Remove all from this group
      setFormData(prev => ({
        ...prev,
        faixas_etarias_permitidas: current.filter(c => !codigos.includes(c))
      }));
    } else {
      // Add all from this group
      const newSelection = [...new Set([...current, ...codigos])];
      setFormData(prev => ({ ...prev, faixas_etarias_permitidas: newSelection }));
    }
  };
  
  // Select all faixas
  const selectAllFaixas = () => {
    const allCodigos = faixasEtarias.map(f => f.codigo);
    setFormData(prev => ({ ...prev, faixas_etarias_permitidas: allCodigos }));
  };
  
  // Clear all faixas
  const clearAllFaixas = () => {
    setFormData(prev => ({ ...prev, faixas_etarias_permitidas: [] }));
  };
  
  // Auto-suggest faixas based on categoria
  const suggestFaixasForCategoria = (categoria) => {
    const suggestions = {
      'Infância': ['bebes_0_12m', 'bebes_13_24m', 'criancas_3_5a', 'criancas_6_10a', 'jovens_11_14a', 'jovens_15_18a'],
      'Idosos': ['idosos_65_74a', 'idosos_75_84a', 'idosos_85plus'],
      'Família': faixasEtarias.map(f => f.codigo), // All
      'Deficiência': faixasEtarias.map(f => f.codigo), // All
      'Saúde Mental': ['adultos_19_30a', 'adultos_31_50a', 'adultos_51_64a', 'idosos_65_74a', 'idosos_75_84a', 'idosos_85plus'],
      'Sem-Abrigo': ['jovens_15_18a', 'adultos_19_30a', 'adultos_31_50a', 'adultos_51_64a', 'idosos_65_74a', 'idosos_75_84a', 'idosos_85plus'],
      'Outros': faixasEtarias.map(f => f.codigo) // All
    };
    return suggestions[categoria] || [];
  };
  
  // When categoria changes, suggest faixas
  const handleCategoriaChange = (categoria) => {
    setFormData(prev => {
      const suggested = suggestFaixasForCategoria(categoria);
      return { 
        ...prev, 
        categoria,
        faixas_etarias_permitidas: prev.faixas_etarias_permitidas.length === 0 ? suggested : prev.faixas_etarias_permitidas
      };
    });
  };

  const handleSubmit = async () => {
    if (!formData.nome || !formData.categoria) {
      toast.error('Nome e categoria são obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingValencia 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/valencias/${editingValencia.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/valencias`;
      
      const response = await fetch(url, {
        method: editingValencia ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingValencia ? 'Resposta social atualizada' : 'Resposta social criada');
        setModalOpen(false);
        loadValencias();
      } else {
        toast.error('Erro ao guardar resposta social');
      }
    } catch (error) {
      toast.error('Erro ao guardar resposta social');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (valencia) => {
    if (!window.confirm(`Tem certeza que deseja desativar a resposta social "${valencia.nome}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/configuracoes/valencias/${valencia.id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Resposta social desativada');
        loadValencias();
      }
    } catch (error) {
      toast.error('Erro ao desativar resposta social');
    }
  };

  const filteredValencias = valencias.filter(v => {
    const matchSearch = v.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       v.codigo_crm?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategoria = filterCategoria === 'Todas' || v.categoria === filterCategoria;
    return matchSearch && matchCategoria;
  });

  const categoriaStats = CATEGORIAS.reduce((acc, cat) => {
    acc[cat] = valencias.filter(v => v.categoria === cat && v.ativo !== false).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="valencias-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Configuração de Respostas Sociais</h1>
          <p className="text-slate-500 dark:text-slate-400">Gestão de tipos de respostas sociais</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadValencias} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={openCreate} data-testid="new-valencia-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nova Resposta Social
          </Button>
        </div>
      </div>

      {/* Stats by Category */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {CATEGORIAS.map((cat) => (
          <Card 
            key={cat} 
            className={`cursor-pointer hover:shadow-md transition-shadow ${filterCategoria === cat ? 'border-[#0066CC] border-2' : ''}`}
            onClick={() => setFilterCategoria(filterCategoria === cat ? 'Todas' : cat)}
          >
            <CardContent className="pt-4 text-center">
              <p className="text-xl font-bold text-slate-900 dark:text-white">{categoriaStats[cat] || 0}</p>
              <p className="text-xs text-slate-500 truncate">{cat}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por nome ou código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={filterCategoria} onValueChange={setFilterCategoria}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Todas">Todas as categorias</SelectItem>
                {CATEGORIAS.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Faixas Etárias</TableHead>
                  <TableHead>Cap. Padrão</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredValencias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Nenhuma resposta social encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredValencias.map((valencia) => {
                    const faixasPermitidas = valencia.faixas_etarias_permitidas || [];
                    const numFaixas = faixasPermitidas.length;
                    const totalFaixas = faixasEtarias.length;
                    
                    return (
                      <TableRow key={valencia.id} className="hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`valencia-row-${valencia.id}`}>
                        <TableCell className="font-mono text-sm">{valencia.codigo_crm}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Heart className="w-4 h-4 text-pink-500" />
                            <span className="font-medium dark:text-white">{valencia.nome}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{valencia.categoria}</Badge>
                        </TableCell>
                        <TableCell>
                          {numFaixas === 0 ? (
                            <span className="text-xs text-slate-400">Todas</span>
                          ) : numFaixas === totalFaixas ? (
                            <Badge className="bg-green-100 text-green-700">Todas ({numFaixas})</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">{numFaixas} de {totalFaixas}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span>{valencia.capacidade_padrao}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={valencia.ativo !== false ? 'bg-green-500' : 'bg-red-500'}>
                            {valencia.ativo !== false ? 'Ativa' : 'Inativa'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(valencia)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(valencia)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              {editingValencia ? 'Editar Resposta Social' : 'Nova Resposta Social'}
            </DialogTitle>
            <DialogDescription>
              {editingValencia ? 'Atualize os dados da resposta social' : 'Adicione uma nova resposta social'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="codigo_crm">Código CRM</Label>
                <Input
                  id="codigo_crm"
                  value={formData.codigo_crm}
                  onChange={(e) => setFormData({ ...formData, codigo_crm: e.target.value })}
                  placeholder="Ex: A101"
                />
              </div>
              <div>
                <Label htmlFor="capacidade">Capacidade Padrão</Label>
                <Input
                  id="capacidade"
                  type="number"
                  value={formData.capacidade_padrao}
                  onChange={(e) => setFormData({ ...formData, capacidade_padrao: parseInt(e.target.value) || 50 })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome da resposta social"
              />
            </div>

            <div>
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descrição da resposta social"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria *</Label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={handleCategoriaChange}
                >
                  <SelectTrigger data-testid="select-categoria">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Público Alvo</Label>
                <Select 
                  value={formData.publico_alvo} 
                  onValueChange={(v) => setFormData({ ...formData, publico_alvo: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {PUBLICO_ALVO.map(pub => (
                      <SelectItem key={pub} value={pub}>{pub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Faixas Etárias Permitidas */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Faixas Etárias Permitidas</Label>
                  <p className="text-xs text-slate-500">Selecione as faixas etárias que esta resposta social atende</p>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllFaixas}>
                    Todas
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={clearAllFaixas}>
                    Limpar
                  </Button>
                </div>
              </div>
              
              {loadingFaixas ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="border rounded-lg p-3 space-y-3 max-h-[200px] overflow-y-auto">
                  {Object.entries(faixasPorGrupo).map(([grupo, grupoData]) => {
                    const faixas = grupoData?.faixas || [];
                    const GrupoIcon = GRUPO_ICONS[grupo] || Users;
                    const grupoColor = GRUPO_COLORS[grupo] || 'bg-slate-100 text-slate-700';
                    const codigos = faixas.map(f => f.codigo);
                    const selectedInGrupo = codigos.filter(c => 
                      formData.faixas_etarias_permitidas?.includes(c)
                    ).length;
                    const allSelectedInGrupo = codigos.length > 0 && selectedInGrupo === codigos.length;
                    
                    return (
                      <div key={grupo} className="space-y-2">
                        <div 
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:opacity-80 ${grupoColor}`}
                          onClick={() => toggleGrupo(grupo, faixas)}
                        >
                          <Checkbox 
                            checked={allSelectedInGrupo}
                            className="pointer-events-none"
                          />
                          <GrupoIcon className="w-4 h-4" />
                          <span className="font-medium capitalize">{grupoData?.nome || grupo}</span>
                          <Badge variant="outline" className="ml-auto text-xs">
                            {selectedInGrupo}/{codigos.length}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-1 pl-6">
                          {faixas.map(faixa => {
                            const isSelected = formData.faixas_etarias_permitidas?.includes(faixa.codigo);
                            return (
                              <div 
                                key={faixa.id}
                                className={`flex items-center gap-2 p-1.5 rounded text-sm cursor-pointer hover:bg-slate-100 ${isSelected ? 'bg-blue-50' : ''}`}
                                onClick={() => toggleFaixaEtaria(faixa.codigo)}
                              >
                                <Checkbox 
                                  checked={isSelected}
                                  className="pointer-events-none"
                                />
                                <span className={isSelected ? 'text-blue-700' : 'text-slate-600'}>
                                  {faixa.nome}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {formData.faixas_etarias_permitidas?.length > 0 && (
                <p className="text-xs text-slate-500">
                  {formData.faixas_etarias_permitidas.length} de {faixasEtarias.length} faixas selecionadas
                </p>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div>
                <Label>Resposta Social Ativa</Label>
                <p className="text-xs text-slate-500">Desativar remove de novas associações</p>
              </div>
              <Switch
                checked={formData.ativo}
                onCheckedChange={(checked) => setFormData({ ...formData, ativo: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="save-valencia-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingValencia ? 'Guardar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
